import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Compass, Globe, Hash, User, RefreshCw, MessageCircle, Repeat, Zap, ChevronDown, ChevronUp, BookOpen, ThumbsUp, ThumbsDown } from 'lucide-react'
import { useLocalFeed } from '../hooks/useLocalFeed'
import { useAllFeed } from '../hooks/useAllFeed'
import { useLongFormContent } from '../hooks/useLongFormContent'
import { useNostrProfile } from '../hooks/useNostrProfile'
import { useReplies } from '../hooks/useReplies'
import { useReactions } from '../hooks/useReactions'
import { nip19, finalizeEvent } from 'nostr-tools'
import { useAdmin } from '../contexts/AdminContext'
import { UserAvatar } from '../components/Common/UserAvatar'
import { FeedMedia } from '../components/Feed/FeedMedia'
import { ReplyModal } from '../components/Reply/ReplyModal'
import { ZapModal } from '../components/Zap/ZapModal'
import { useAuth } from '../contexts/AuthContext'
import { reactToEvent } from '../lib/nostr'
import { getNostrPool } from '../lib/nostrPool'
import { RELAYS } from '../config/constants'

type FeedType = 'mavililer' | 'local' | 'worldwide'

interface FeedItemProps {
  event: any
}

function FeedItem({ event }: FeedItemProps) {
  const { metadata } = useNostrProfile(event.pubkey)
  const [showReplies, setShowReplies] = useState(false)
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false)
  const [isZapModalOpen, setIsZapModalOpen] = useState(false)
  const { sk, pubkey: currentUserPubkey } = useAuth()
  const { replies, isLoading: loadingReplies } = useReplies(showReplies ? event.id : null)
  const { upvotes, downvotes, userVotes } = useReactions(event.id)

  const imageRegex = /(https?:\/\/[^\s]+?\.(?:jpg|jpeg|png|webp|gif|svg|avif))/gi;
  const images = event.content.match(imageRegex) || [];

  const contentWithoutImages = event.content.replace(imageRegex, '').trim();

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (minutes < 1) return 'az önce'
    if (minutes < 60) return `${minutes}d`
    if (hours < 24) return `${hours}s`
    if (days < 7) return `${days}g`
    return date.toLocaleDateString('tr-TR')
  }

  const { settings } = useAdmin()

  // Filter out if deleted or banned
  if (settings.deletedEventIds.includes(event.id)) return null
  if (settings.bannedWords.some(word => event.content.toLowerCase().includes(word.toLowerCase()))) return null

  const npub = nip19.npubEncode(event.pubkey)

  const replyTo = event.tags.find((t: any[]) => t[0] === 'p')?.[1];
  const { metadata: replyToMetadata } = useNostrProfile(replyTo || null);

  const [isProcessingVote, setIsProcessingVote] = useState(false);

  const handleVote = async (type: '+' | '-') => {
    if (!sk && !window.nostr) {
      alert('Oy vermek için giriş yapmalısınız.')
      return
    }

    if (isProcessingVote) return;

    const currentUserVote = currentUserPubkey ? userVotes[currentUserPubkey || '']?.content : null;

    // Toggle off if clicking same vote
    if (currentUserVote === type) {
      // Ideally we would delete the vote here, but for simplicity/consistency with previous logic, we might just return or implement delete.
      // Let's implement delete for better UX if possible, or just allow switching.
      // For now, let's just return to avoid re-voting same thing.
      // Actually EntryCard handles toggle off. Let's do simple switching/voting for now to match minimal logic.
      return
    }

    setIsProcessingVote(true);
    try {
      await reactToEvent(event.id, event.pubkey, type, sk)
    } catch (e) {
      console.error('Vote failed:', e)
    } finally {
      setIsProcessingVote(false);
    }
  }

  const handleRepost = async () => {
    if (!sk && !window.nostr) {
      alert('Repost yapmak için giriş yapmalısınız.')
      return
    }

    // Simple confirmation
    if (!confirm('Bu içeriği paylaşmak istiyor musunuz?')) {
      return
    }

    try {
      const pool = getNostrPool()

      // NIP-18: Repost (kind 6)
      const repostEvent = {
        kind: 6,
        created_at: Math.floor(Date.now() / 1000),
        tags: [
          ['e', event.id],
          ['p', event.pubkey]
        ],
        content: JSON.stringify(event)
      }

      const signed = sk
        ? finalizeEvent(repostEvent as any, sk)
        : await window.nostr!.signEvent(repostEvent)

      await pool.publish(RELAYS, signed)
      alert('✅ Paylaşıldı!')
    } catch (e) {
      console.error('Repost failed:', e)
      alert('❌ Paylaşım başarısız oldu.')
    }
  }

  const isExternal = !event.tags.some((t: any[]) => t[0] === 't' && ['mavililer', 'mavililersözlük', 'mavililersozluk'].includes(t[1].toLowerCase()));
  const profileLink = isExternal ? `/u/${npub}` : `/profile/${event.pubkey}`;

  return (
    <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 hover:bg-surface-50/50 dark:hover:bg-surface-800/30 transition-all overflow-hidden">
      <div className="p-4 md:p-6">
        <div className="flex gap-4">
          <Link to={profileLink} className="flex-shrink-0">
            <UserAvatar
              src={metadata?.picture}
              name={metadata?.display_name || metadata?.name || event.pubkey}
              size="lg"
              isExternal={isExternal}
            />
          </Link>

          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <Link to={profileLink} className="font-poppins font-bold text-gray-900 dark:text-white hover:underline truncate">
                  {metadata?.display_name || metadata?.name || 'Anonim'}
                </Link>
                <span className="text-gray-500 text-sm truncate">
                  {metadata?.nip05 || `@${npub.slice(0, 8)}...`}
                </span>
                <span className="text-gray-400 text-sm flex-shrink-0">·</span>
                <span className="text-gray-500 text-sm flex-shrink-0">
                  {formatDate(event.created_at)}
                </span>
              </div>
            </div>

            {/* Replying to... */}
            {replyTo && (
              <div className="text-xs text-mavi-500/80 mb-2 font-poppins">
                yanıtlandı: <span className="font-bold">@{replyToMetadata?.display_name || replyToMetadata?.name || replyTo.slice(0, 8)}</span>
              </div>
            )}

            {/* Content */}
            <div className="font-merriweather text-[0.95rem] leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words mb-3">
              {contentWithoutImages.split(/(\s+)/).map((word: string, i: number) => {
                // Check if word is a hashtag
                if (word.match(/^#[\w\u00C0-\u017F]+$/)) {
                  const tag = word.slice(1); // Remove #
                  return (
                    <Link
                      key={i}
                      to={`/hashtag/${encodeURIComponent(tag)}`}
                      className="text-mavi-600 dark:text-mavi-400 hover:underline hover:text-mavi-700 dark:hover:text-mavi-300 font-medium transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {word}
                    </Link>
                  );
                }
                return <span key={i}>{word}</span>;
              })}
            </div>

            {/* Images */}
            <FeedMedia urls={images} />

            {/* Interaction Buttons */}
            <div className="flex items-center justify-between mt-4 max-w-md text-gray-500 dark:text-gray-400">
              <button
                onClick={() => setIsReplyModalOpen(true)}
                className="flex items-center gap-2 hover:text-mavi-500 transition-colors group"
                title="Yanıtla"
              >
                <div className="p-2 group-hover:bg-mavi-500/10 rounded-full transition-colors">
                  <MessageCircle className="w-4.5 h-4.5" />
                </div>
                <span className="text-sm">Yanıtla</span>
              </button>

              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVote('+'); }}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full transition-all group ${userVotes[currentUserPubkey || '']?.content === '+' ? 'text-green-600 font-bold' : 'text-gray-500 hover:text-green-600 hover:bg-green-500/5'}`}
                title="Beğen"
              >
                <ThumbsUp className={`w-4.5 h-4.5 group-hover:scale-110 transition-transform ${userVotes[currentUserPubkey || '']?.content === '+' ? 'fill-green-600/20' : ''}`} />
                {upvotes > 0 && <span className="text-sm font-medium">{upvotes}</span>}
              </button>

              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVote('-'); }}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full transition-all group ${userVotes[currentUserPubkey || '']?.content === '-' ? 'text-red-600 font-bold' : 'text-gray-500 hover:text-red-600 hover:bg-red-500/5'}`}
                title="Beğenme"
              >
                <ThumbsDown className={`w-4.5 h-4.5 group-hover:scale-110 transition-transform ${userVotes[currentUserPubkey || '']?.content === '-' ? 'fill-red-600/20' : ''}`} />
                {downvotes > 0 && <span className="text-sm font-medium">{downvotes}</span>}
              </button>

              <button
                onClick={handleRepost}
                className="flex items-center gap-2 hover:text-green-500 transition-colors group"
                title="Repost"
              >
                <div className="p-2 group-hover:bg-green-500/10 rounded-full transition-colors">
                  <Repeat className="w-4.5 h-4.5" />
                </div>
              </button>

              <button
                onClick={() => setIsZapModalOpen(true)}
                className="flex items-center gap-2 hover:text-orange-500 transition-colors group"
                title="Zap"
              >
                <div className="p-2 group-hover:bg-orange-500/10 rounded-full transition-colors">
                  <Zap className="w-4.5 h-4.5" />
                </div>
              </button>

              <button
                onClick={() => setShowReplies(!showReplies)}
                className={`flex items-center gap-1 hover:text-mavi-500 transition-colors group ${showReplies ? 'text-mavi-500' : ''}`}
                title="Yorumları Göster"
              >
                <div className="p-2 group-hover:bg-mavi-500/10 rounded-full transition-colors">
                  {showReplies ? <ChevronUp className="w-4.5 h-4.5" /> : <ChevronDown className="w-4.5 h-4.5" />}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Replies Section */}
      <AnimatePresence>
        {showReplies && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-surface-100 dark:border-surface-800 bg-surface-50/30 dark:bg-surface-900/50"
          >
            <div className="p-4 space-y-4">
              {loadingReplies ? (
                <div className="flex justify-center p-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-mavi-500" />
                </div>
              ) : replies.length > 0 ? (
                replies.map(reply => (
                  <div key={reply.id} className="flex gap-3">
                    <div className="flex-shrink-0 pt-1">
                      <UserAvatar
                        src={null} // We could fetch metadata for each reply if needed
                        name={reply.pubkey.slice(0, 8)}
                        size="sm"
                        isExternal={true}
                      />
                    </div>
                    <div className="flex-1 bg-white dark:bg-surface-800 p-3 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-sm">
                      <p className="text-sm dark:text-gray-200">{reply.content}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-sm text-gray-500 py-4">Henüz yorum yok.</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ReplyModal
        isOpen={isReplyModalOpen}
        onClose={() => setIsReplyModalOpen(false)}
        replyToEvent={event}
        authorName={metadata?.display_name || metadata?.name || 'Anonim'}
      />
      <ZapModal
        isOpen={isZapModalOpen}
        onClose={() => setIsZapModalOpen(false)}
        targetPubkey={event.pubkey}
        targetName={metadata?.display_name || metadata?.name || 'Anonim'}
        eventId={event.id}
      />
    </div>
  )
}

export function FlowPage() {
  const { type } = useParams<{ type: string }>()
  const feedType = (type || 'mavililer') as FeedType

  const { localEvents, isLoading: localLoading, loadMore: loadMoreLocal, refetch: refetchLocal } = useLocalFeed(20)
  const { allEvents: yerelEvents, isLoading: yerelLoading, loadMore: loadMoreYerel, refetch: refetchYerel } = useAllFeed(20, 'tr')
  const { allEvents: globalEvents, isLoading: globalLoading, loadMore: loadMoreGlobal, refetch: refetchGlobal } = useAllFeed(20, 'all')
  const { articles } = useLongFormContent(10)

  const events = feedType === 'mavililer' ? localEvents : feedType === 'local' ? yerelEvents : globalEvents
  const isLoading = feedType === 'mavililer' ? localLoading : feedType === 'local' ? yerelLoading : globalLoading
  const loadMore = feedType === 'mavililer' ? loadMoreLocal : feedType === 'local' ? loadMoreYerel : loadMoreGlobal
  const refetch = feedType === 'mavililer' ? refetchLocal : feedType === 'local' ? refetchYerel : refetchGlobal

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-3xl mx-auto space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col gap-6 sticky top-0 bg-surface-50/80 dark:bg-surface-950/80 backdrop-blur-md z-10 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-mavi-500/10 rounded-xl">
              <Compass className="w-6 h-6 text-mavi-500" />
            </div>
            <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">
              akış
            </h2>
          </div>
          <button
            onClick={refetch}
            disabled={isLoading}
            className="p-3 rounded-full hover:bg-surface-200 dark:hover:bg-surface-800 transition-all disabled:opacity-50 border border-surface-200 dark:border-surface-800"
            title="Yenile"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Improved Tabs as Links */}
        <div className="flex items-center p-1.5 bg-surface-100/50 dark:bg-surface-800/50 rounded-2xl w-full">
          <Link
            to="/flow/mavililer"
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-poppins font-bold text-sm transition-all ${feedType === 'mavililer'
              ? 'bg-white dark:bg-surface-700 shadow-md text-mavi-600 dark:text-mavi-400 scale-[1.02]'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
          >
            <Hash className="w-4 h-4" />
            Mavililer
          </Link>
          <Link
            to="/flow/local"
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-poppins font-bold text-sm transition-all ${feedType === 'local'
              ? 'bg-white dark:bg-surface-700 shadow-md text-mavi-600 dark:text-mavi-400 scale-[1.02]'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
          >
            <User className="w-4 h-4" />
            Yerel
          </Link>
          <Link
            to="/flow/worldwide"
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-poppins font-bold text-sm transition-all ${feedType === 'worldwide'
              ? 'bg-white dark:bg-surface-700 shadow-md text-mavi-600 dark:text-mavi-400 scale-[1.02]'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
          >
            <Globe className="w-4 h-4" />
            Global
          </Link>
        </div>
      </div>

      {/* Articles Section (Shown only on Mavililer or Yerel) */}
      {(feedType === 'mavililer' || feedType === 'local') && articles.length > 0 && !isLoading && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-mavi-500">
            <BookOpen className="w-5 h-5 transition-transform group-hover:scale-110" />
            <h3 className="font-poppins font-bold text-lg">Öne Çıkan Makaleler</h3>
          </div>
          <div className="flex flex-col gap-4">
            {articles.slice(0, 2).map(article => (
              <Link
                key={article.id}
                to={`/article/${nip19.neventEncode({ id: article.id })}`}
                className="group bg-gradient-to-br from-mavi-500 to-mavi-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-mavi-500/20 transition-all hover:-translate-y-1"
              >
                <h4 className="font-poppins font-bold text-xl mb-2 line-clamp-1">
                  {article.tags.find((t: any[]) => t[0] === 'title')?.[1] || 'Başlıksız'}
                </h4>
                <p className="text-white/80 text-sm line-clamp-2 font-merriweather">
                  {article.content.slice(0, 150)}...
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Main Feed Content */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white dark:bg-surface-900 rounded-2xl p-6 h-48 border border-surface-200 dark:border-surface-800" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-surface-900 rounded-3xl border border-surface-200 dark:border-surface-800 shadow-sm">
            <div className="w-20 h-20 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Compass className="w-10 h-10 text-gray-300 dark:text-gray-600" />
            </div>
            <h3 className="text-xl font-poppins font-bold text-gray-900 dark:text-white mb-2">Henüz içerik yok</h3>
            <p className="text-gray-500 dark:text-gray-400">Bu kategoride gösterilecek bir şey bulamadık.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {events.map((event: any) => (
              <FeedItem key={event.id} event={event} />
            ))}

            <button
              onClick={loadMore}
              className="w-full py-4 mt-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 font-poppins font-bold text-mavi-500 hover:bg-mavi-50/50 dark:hover:bg-mavi-950/20 transition-all"
            >
              Daha fazla yükle
            </button>
          </div>
        )}
      </div>
    </motion.div>
  )
}
